import { useCallback, useEffect, useRef, useState } from 'react';
import { PendingOutput, getRawModelPendingOutputs } from '@/app/assets/actions/rawModelPendingOutputs';
import { OptimisticAssetsAction } from '@/app/assets/components/table/hooks/useOptimisticAssets';
import { QueryObserver, useQuery, useQueryClient } from '@tanstack/react-query';
import { MAIN_ASSET_QUERY_KEY } from '../../../hooks/useAssetsQuery';
import { useOnboarding } from '@/app/contexts/OnboardingContext/OnboardingContext';
import { OnboardingFlows } from '@/app/contexts/OnboardingContext/constants';
import { showErrorNotification } from '@/app/components/shared/notifications';

const PENDING_ASSETS_KEY = ['pendingOutputs'];

export const usePendingOutputs = (dispatchOptimisticAssetsAction: (action: OptimisticAssetsAction) => void) => {
  const [enabled, setEnabled] = useState(true);
  const errorShownRef = useRef(false);
  const [previousPendingOutputs, setPreviousPendingOutputs] = useState<PendingOutput[]>([]);
  const queryClient = useQueryClient();
  const onJobEndCallbacks = useRef<OnJobEndCallbacks>({});

  const addOnJobEndCallback = useCallback(
    (key: string, callback: (job: PendingOutput) => void) => {
      onJobEndCallbacks.current[key] = callback;
    },
    [onJobEndCallbacks]
  );
  const { runFlow } = useOnboarding();

  useEffect(() => {
    errorShownRef.current = false;
  }, [enabled]);

  const fetchPendingOutputs = useCallback(async () => {
    try {
      const pendingOutputsResponse = await getRawModelPendingOutputs();
      if (!enabled) {
        return previousPendingOutputs;
      }
      if (!pendingOutputsResponse.some((p) => p.optimisationStatus !== 'failed')) {
        setEnabled(false);
        if (pendingOutputsResponse.length === 0) {
          dispatchOptimisticAssetsAction({
            action: 'addFinishedOutput',
            payload: pendingOutputsResponse,
          });
          await queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.includes(MAIN_ASSET_QUERY_KEY),
          });
          return pendingOutputsResponse;
        }
      }

      return pendingOutputsResponse;
    } catch (error) {
      if (!errorShownRef.current) {
        showErrorNotification({
          title: 'Error',
          message:
            'An error occurred while fetching running jobs: ' +
            (error instanceof Error ? error.message : 'Unknown error'),
        });
        errorShownRef.current = true;
      }
      return [];
    }
  }, [dispatchOptimisticAssetsAction, queryClient, enabled, previousPendingOutputs]);

  const { data: pendingOutputs } = useQuery<PendingOutput[]>({
    queryKey: PENDING_ASSETS_KEY,
    queryFn: fetchPendingOutputs,
    refetchInterval: 3000,
    staleTime: Infinity,
    enabled,
    initialData: [],
    throwOnError: false, // Should never throw anyway because of the try catch in the fetchPendingOutputs
  });

  useEffect(() => {
    const areDifferentLength = previousPendingOutputs.length !== pendingOutputs.length;
    const areSameIdx = pendingOutputs.every((output) =>
      previousPendingOutputs.find((prevOutput) => prevOutput.outputId === output.outputId)
    );

    if (previousPendingOutputs.length > pendingOutputs.length) {
      const finishedJobs = previousPendingOutputs.filter(
        (previousPending) => !pendingOutputs.find((pending) => pending.outputId === previousPending.outputId)
      );

      finishedJobs.forEach((job) => {
        Object.values(onJobEndCallbacks.current).forEach((callback) => callback(job));
      });

      if (finishedJobs.length > 0) {
        runFlow(OnboardingFlows.EXPAND_ASSET, { id: finishedJobs[0].outputId });
      }

      if (pendingOutputs.length !== 0) {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes(MAIN_ASSET_QUERY_KEY),
        });
        dispatchOptimisticAssetsAction({
          action: 'addFinishedOutput',
          payload: finishedJobs,
        });
        return setPreviousPendingOutputs(pendingOutputs);
      }
    }

    if (areDifferentLength || !areSameIdx) {
      setPreviousPendingOutputs(pendingOutputs);
    }
  }, [pendingOutputs, dispatchOptimisticAssetsAction, previousPendingOutputs, queryClient, runFlow]);

  return {
    startFetchingPendingOutputs: () => setEnabled(true),
    pendingOutputs,
    addOnJobEndCallback,
  };
};

export const usePendingOutputsConsumer = () => {
  const queryClient = useQueryClient();
  const { data: pendingWorkflows } = useQuery<PendingOutput[]>({
    queryKey: PENDING_ASSETS_KEY,
    queryFn: () => {
      throw Error('This query is only used to observe cache value and must be always disabled');
    },
    enabled: false,
    throwOnError: true,
  });

  const getPendingWorkflowProgress = (workflowId: number, rawAssetsIds: number[]) => {
    const filteredData = (pendingWorkflows ?? []).filter(
      (workflow) =>
        workflow.workflowId === workflowId &&
        rawAssetsIds.includes(workflow.rawModelId) &&
        workflow.optimisationStatus !== 'failed'
    );
    if (filteredData.length === 0) {
      return null;
    }
    const totalProgress = filteredData.reduce((sum, workflow) => sum + workflow.progress, 0);
    const averageProgress = totalProgress / filteredData.length;
    return averageProgress;
  };

  const addOptimisticOutput = (output: PendingOutput) => {
    queryClient.setQueryData<PendingOutput[]>(['pendingOutputs'], (prev) => (prev ? prev.concat(output) : [output]));
  };

  const getAssetProgress = (id: number) => {
    const filteredData = (pendingWorkflows ?? []).filter(
      (workflow) => workflow.rawModelId === id && workflow.optimisationStatus !== 'failed'
    );
    if (filteredData.length === 0) {
      return null;
    }
    const totalProgress = filteredData.reduce((sum, workflow) => sum + workflow.progress, 0);
    const operationsCount = filteredData.length;
    const averageProgress = totalProgress / operationsCount;
    const operationsInProgress = filteredData.filter((outputs) => outputs.progress !== 0).length;

    return { progress: averageProgress, operationsInProgress, operationsCount };
  };

  return { getPendingWorkflowProgress, addOptimisticOutput, getAssetProgress };
};

type OnJobEndCallbacks = {
  [key: string]: (job: PendingOutput) => void;
};
